import { CANVAS_URN_TYPES, canvasUrnIsValid } from "@/types/agenticUiState.ts";
import { safeParseURN } from "@/types/urn.ts";

/**
 * The canvas content language: parse and classify the content tokens the agent
 * links to and the canvas renders. A token is a canonical entity URN
 * (`urn:bb:<type>:<id>`) addressing a source ent (`urn:bb:source:<id>`), a
 * contact (`urn:bb:contact:<id>`), a routine (`urn:bb:routine:<id>`), a thread's
 * participant roster (`urn:bb:threadParticipants:<threadId>`), or a heavy artifact
 * (`urn:bb:<artifactType>:<id>`). Which card a source renders into is a property
 * of the source subtype, resolved at render time from `/api/a/sources/:id` — not
 * knowable from the token alone, since a communication and an event are
 * byte-identical `urn:bb:source:<id>` URNs.
 */

/** How a canvas token surfaces: the source modal, the contact modal, the communication-thread modal, the routine modal, the participants modal, or the artifact modal. */
export type CanvasContentKind =
  | "source"
  | "artifact"
  | "contact"
  | "communicationThread"
  | "routine"
  | "threadParticipants";

export type ParsedCanvasContent = {
  kind: CanvasContentKind;
  type: string;
  id: string;
};

/** Map a canvas-renderable URN type to its render kind. */
function kindForUrnType(type: string): CanvasContentKind | null {
  if (type === "source") return "source";
  if (type === "contact") return "contact";
  // Addressed by any one message's source id, which the server expands to the
  // whole conversation. Distinct from `source` on purpose: that renders the one
  // message a link cited, this renders the exchange it belongs to.
  //
  // Deliberately absent from `CANVAS_URN_TYPES`, unlike every other branch here.
  // That set's members fall through to the artifact rule in two places — the
  // last line of this function, and `resolveMessageLink` — and the others are
  // saved from it by an entry in `URN_ROUTES`. This type has no page of its own
  // to name there, so listing it would resolve a link to
  // `/artifacts/communicationThread/<id>`: an artifact URL for something that is
  // not an artifact. It is reached by a click that builds the URN directly, and
  // the agent cites the single `source` instead.
  if (type === "communicationThread") return "communicationThread";
  if (type === "routine") return "routine";
  if (type === "threadParticipants") return "threadParticipants";
  // Every remaining canvas type is an artifact type, so this must stay last: a
  // new type added to the set without a branch above lands in the artifact
  // modal, which would fail to load it rather than say why.
  if (CANVAS_URN_TYPES.has(type)) return "artifact";
  return null;
}

/**
 * Parse a canvas content token into its kind, type, and id, or null when it is
 * not a canvas-renderable URN.
 *
 * Tokens are canonical entity URNs (`urn:bb:<type>:<id>`), parsed by the shared
 * {@link safeParseURN}. Returns null for a non-URN token (bare `type:id`, a
 * path, an http link), an id in a form its type isn't addressed by, or a type
 * that isn't canvas content.
 */
export function parseCanvasContent(raw: string): ParsedCanvasContent | null {
  const urn = safeParseURN(raw);
  if (!urn) return null;
  const kind = kindForUrnType(urn.type);
  if (!kind) return null;
  // Check the id form per type rather than demanding a numeric id of
  // everything. `urn:bb:source:system:foo` parses but can't be loaded by id, so
  // it stays rejected; a routine is addressed by UUID or `system:<slug>`, so
  // for that type those are the loadable forms and a bare number is not special.
  if (!canvasUrnIsValid(raw, urn.type)) return null;
  return { kind, type: urn.type, id: urn.id };
}

/**
 * Map a canonical entity page path to the canvas token (URN) to present in place,
 * or null when it has no canvas representation. Inverse of the markdown link
 * builders; query/hash stripped. `/communications/:id` and `/calendar/event/:id`
 * share the source ent id, so they map like `/sources/:id`. A bare
 * `/threads/:id` → null (navigate); the reference card that points at a thread
 * is addressed via `/artifacts/threadArtifact/:id`, and its participant roster
 * via `/threads/:id/participants`. Null when the token isn't canvas-renderable
 * ({@link parseCanvasContent}).
 */
export function canvasContentForPath(path: string): string | null {
  const [, segment, first, second] = path.split(/[?#]/)[0].split("/");
  const urn = (() => {
    switch (segment) {
      case "contacts":
        return first ? `urn:bb:contact:${first}` : null;
      case "sources":
        return first ? `urn:bb:source:${first}` : null;
      // A conversation is a view of one of its messages rather than an ent of
      // its own, so it is addressed by that message's source id with `/thread`
      // after it — the same shape `/threads/:id/participants` uses. The bare
      // `/communications/:id` stays the single message it names.
      case "communications":
        if (!first) return null;
        return second === "thread"
          ? `urn:bb:communicationThread:${first}`
          : `urn:bb:source:${first}`;
      case "artifacts":
        // Type-qualified (`/artifacts/:type/:id`), so it round-trips to the
        // canonical `urn:bb:<type>:<id>`.
        return first && second ? `urn:bb:${first}:${second}` : null;
      // A thread's participant roster is addressed by the thread's own id — it
      // is a view of the thread, not an ent of its own. The bare `/threads/:id`
      // falls to the default below and stays real navigation: the thread is a
      // page, not a card.
      case "threads":
        return first && second === "participants" ? `urn:bb:threadParticipants:${first}` : null;
      // `/calendar/event/:id` addresses an event source by the same id
      // `/sources/:id` uses, so it presents identically — a card at any depth. A
      // `/calendar?…` focus link isn't canvas content — it moves the base
      // calendar, which isn't a stack layer.
      case "calendar":
        return first === "event" && second ? `urn:bb:source:${second}` : null;
      // The routines list lives under the settings screen, so a routine's page
      // path is `/settings/routines/:id`. Only that tab is canvas content —
      // every other settings tab is a page, not an entity.
      case "settings":
        return first === "routines" && second ? `urn:bb:routine:${second}` : null;
      default:
        return null;
    }
  })();
  if (!urn) return null;
  return parseCanvasContent(urn) ? urn : null;
}
